import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFeatures } from '../contexts/FeatureContext';
import { getFeatureForRoute } from '../utils/featureRouteMapping';
import { FeatureNotAvailable } from './FeatureNotAvailable';

/**
 * FeatureGuard Component
 * Protects routes based on feature flags
 * Shows "Feature Not Available" page if feature is disabled
 * 
 * Usage:
 * <Route path="/savings" element={<FeatureGuard><SavingsPage /></FeatureGuard>} />
 * 
 * Or with explicit feature:
 * <Route path="/custom" element={<FeatureGuard feature="custom_feature"><CustomPage /></FeatureGuard>} />
 */
export const FeatureGuard = ({ children, feature: explicitFeature }) => {
  const location = useLocation();
  const { isFeatureEnabled, getFeatureMessage, loading } = useFeatures();
  
  // Show loading state briefly
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Determine which feature to check
  const featureId = explicitFeature || getFeatureForRoute(location.pathname);
  
  // If no feature mapping exists, allow access (unprotected route)
  if (!featureId) {
    return <>{children}</>;
  }
  
  // Check if feature is enabled
  const enabled = isFeatureEnabled(featureId);
  
  // If disabled, show "Feature Not Available" page
  if (!enabled) {
    const message = getFeatureMessage(featureId);
    return <FeatureNotAvailable featureId={featureId} message={message} />;
  }
  
  // Feature is enabled, render children
  return <>{children}</>;
};

/**
 * Hook to check if a feature is available before navigation
 * Usage: const canNavigate = useFeatureCheck('savings');
 */
export const useFeatureCheck = (featureId) => {
  const { isFeatureEnabled } = useFeatures();
  return isFeatureEnabled(featureId);
};
